import { FC, useState } from "react";
import { Helmet } from "react-helmet";
import ReactSelect, { StylesConfig, GroupBase } from "react-select";
import toast from "react-hot-toast";
import { InputTextArea } from "src/styles/Input";
import { ButtonV2 } from "src/styles/ButtonV2";
import { ItemHeading } from "src/styles/ItemHeading";
import { ContentCard } from "src/styles/ContentCard";
import { PageWrapper } from "./ArticleForm/styles";
import { generateOpenAiTtsAudio } from "src/actions/article";

interface VoiceOption {
    label: string;
    value: string;
}

const voiceOptions: VoiceOption[] = [
    { label: "Alloy", value: "alloy" },
    { label: "Echo", value: "echo" },
    { label: "Fable", value: "fable" },
    { label: "Onyx", value: "onyx" },
    { label: "Nova", value: "nova" },
    { label: "Shimmer", value: "shimmer" },
    { label: "Coral", value: "coral" },
    { label: "Ash", value: "ash" },
    { label: "Ballad", value: "ballad" },
    { label: "Sage", value: "sage" },
];

// Custom styles for ReactSelect with string values
const customReactSelectStyles: StylesConfig<VoiceOption, false, GroupBase<VoiceOption>> = {
    control: (base) => {
        return {
            ...base,
            borderRadius: "3px",
            borderWidth: "2px",
            boxShadow: "none",
            borderColor: "#dfe1e6",
            fontSize: "14px",
            color: "#000",
            ":hover": {
                borderColor: "#039be5",
            },
        };
    },
    valueContainer: (base) => {
        return { ...base, height: "42px", padding: "0px" };
    },
    input: (base) => {
        return { ...base, height: "42px", margin: "0px 10px", padding: "0px" };
    },
    singleValue: (base) => {
        return { ...base, padding: "0px", margin: "0px 10px" };
    },
    placeholder: (base) => {
        return { ...base, color: "#7a869a", margin: "0px 10px" };
    },
    menu: (base) => {
        return { ...base, zIndex: 100 };
    },
};

const OpenAiTts: FC = () => {
    const [isGenerating, setIsGenerating] = useState<boolean>(false);
    const [selectedVoice, setSelectedVoice] = useState<VoiceOption | null>(voiceOptions.find((v) => v.value === "coral") || null);
    const [instructions, setInstructions] = useState<string>("");
    const [articleText, setArticleText] = useState<string>("");

    const handleVoiceChange = (option: any) => {
        setSelectedVoice(option);
    };

    const handleInstructionsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setInstructions(e.target.value);
    };

    const handleArticleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setArticleText(e.target.value);
    };

    const isFormValid = (): boolean => {
        return !!selectedVoice && !!articleText.trim();
    };

    const generateAudio = async () => {
        if (!isFormValid() || !selectedVoice) return;

        setIsGenerating(true);

        try {
            const response = await generateOpenAiTtsAudio(selectedVoice.value, articleText.trim(), instructions || undefined);

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                const errorMessage = errorData?.error || "Request failed.";
                throw new Error(`${response.status}: ${errorMessage}`);
            }

            // Handle the audio buffer response
            const audioBuffer = await response.arrayBuffer();
            const blob = new Blob([audioBuffer], { type: "audio/mpeg" });
            const url = window.URL.createObjectURL(blob);

            // Create a download link
            const a = document.createElement("a");
            a.href = url;
            a.download = `openai-tts-${Date.now()}.mp3`;
            document.body.appendChild(a);
            a.click();

            // Clean up
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);

            toast.success("Audio generated successfully!");
        } catch (error) {
            toast.error(error instanceof Error ? error.message : "Request failed.");
        } finally {
            setIsGenerating(false);
        }
    };

    return (
        <PageWrapper>
            {/* 
            // @ts-ignore */}
            <Helmet>
                <title>OpenAI TTS | Noa</title>
            </Helmet>
            <ContentCard>
                <ItemHeading>OpenAI Text-to-Speech</ItemHeading>

                <ItemHeading setMarginTop>Voice</ItemHeading>
                <div style={{ width: "100%", maxWidth: "400px" }}>
                    <ReactSelect styles={customReactSelectStyles} options={voiceOptions} value={selectedVoice} onChange={handleVoiceChange} placeholder="Select a voice" />
                </div>

                <ItemHeading setMarginTop>Instructions (optional)</ItemHeading>
                <InputTextArea
                    color="#7a869a"
                    placeholder="Enter instructions for the voice (optional)"
                    value={instructions}
                    onChange={handleInstructionsChange}
                    style={{ maxWidth: "100%", width: "100%" }}
                />

                <ItemHeading setMarginTop>Article Text</ItemHeading>
                <InputTextArea
                    color="#7a869a"
                    placeholder="Enter the article text to convert to speech"
                    value={articleText}
                    onChange={handleArticleTextChange}
                    style={{ maxWidth: "100%", width: "100%", minHeight: "200px" }}
                />

                <label style={{ color: "#7A869A", display: "block", marginTop: "2px", fontWeight: "400" }}>Note: The maximum length is 4096 characters.</label>

                <ButtonV2 onClick={generateAudio} disabled={!isFormValid() || isGenerating} setMarginTop>
                    {isGenerating ? "Generating..." : "Generate Audio"}
                </ButtonV2>
            </ContentCard>
        </PageWrapper>
    );
};

export default OpenAiTts;
